import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { FaBoxOpen, FaFilter, FaFileExport } from "react-icons/fa";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function ProductSales() {
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ search: "" });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const vendorId = localStorage.getItem("vendorId");

  useEffect(() => {
    const fetchProductSales = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${baseurl}getVendorProductSales/${vendorId}`);
        if (res.data.success) {
          setProducts(res.data.products || []);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error("Error fetching product sales:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    if (vendorId) fetchProductSales();
  }, [vendorId]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) =>
      `${p.name} ${p.sku}`.toLowerCase().includes(filters.search.toLowerCase())
    );
  }, [products, filters.search]);

  // ----------------- Export to Excel -----------------
  const exportToExcel = () => {
    if (!filteredProducts.length) return;

    // Map data for Excel
    const data = filteredProducts.map((p, i) => ({
      "#": i + 1,
      "Product Name": p.name,
      SKU: p.sku,
      Price: p.price,
      MRP: p.mrp,
      "Quantity Sold": p.quantity_sold,
      "Total Revenue": p.total_revenue,
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Product Sales");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Product_Sales_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="main-content">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="breadcrumb-title d-flex align-items-center gap-2">
              <FaBoxOpen /> Product Sales Report
            </h4>
            <div className="d-flex gap-2">
              <button
                className="btn btn-success d-flex align-items-center gap-2"
                onClick={exportToExcel}
              >
                <FaFileExport /> Export Excel
              </button>
              <button
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={() => setShowFilter(!showFilter)}
              >
                <FaFilter /> Filters
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilter && (
            <div className="card p-3 mb-3 shadow-sm border-0">
              <div className="row g-2 align-items-end">
                <div className="col-md-6">
                  <label>Search Product</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Product name or SKU"
                    value={filters.search}
                    onChange={(e) =>
                      setFilters({ ...filters, search: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-3">
                  <button
                    className="btn btn-primary w-100"
                    onClick={() => setShowFilter(false)}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Product Sales Table */}
          <div className="card shadow-sm border-0">
            <div className="card-body table-responsive">
              {loading ? (
                <div className="text-center py-4 text-muted">
                  Loading product sales...
                </div>
              ) : filteredProducts.length > 0 ? (
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Image</th>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Price</th>
                      <th>MRP</th>
                      <th>Quantity Sold</th>
                      <th>Total Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((p, i) => (
                      <tr key={p.product_id}>
                        <td>{i + 1}</td>
                        <td>
                          <img
                            src={p.featured_image ? `${imgurl}${p.featured_image}` : "/noimage.png"}
                            alt={p.name}
                            style={{
                              width: "45px",
                              height: "45px",
                              borderRadius: "6px",
                              objectFit: "cover",
                            }}
                          />
                        </td>
                        <td className="fw-semibold">{p.name}</td>
                        <td>{p.sku}</td>
                        <td>₹{p.price}</td>
                        <td>₹{p.mrp}</td>
                        <td>{p.quantity_sold}</td>
                        <td>₹{p.total_revenue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center text-muted py-4">
                  No product sales data available
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <style jsx>{`
        .table td,
        .table th {
          vertical-align: middle;
        }
      `}</style>
    </>
  );
}

export default ProductSales;
